/**
 * useIssueResolution — IssueResolutionState hook.
 *
 * Tracks per-issue resolution status and pending patches.
 * Mutations update local state immediately (for instant UI feedback) and also
 * persist to SQL Server via the Gate Decisions API (fire-and-forget; errors are
 * logged but do not block the UI).
 *
 * Resolution states:
 *   null            — open (no action taken)
 *   'pending_patch' — user applied a fix; patch staged locally + saved to DB
 *   'deferred'      — user marked as TODO/stub; saved to DB
 *   'suppressed'    — user suppressed finding; saved to DB
 *   'stubbed'       — GENERATE_COMPILE_STUB decision saved; worker creates stub on rerun
 *   'external'      — EXTERNAL_DEPENDENCY recorded; no code fabrication
 *
 * Call `preloadDecisions(rows)` after fetching existing decisions from the API
 * to hydrate the resolution map from persisted DB state.
 */

import { useState, useCallback } from 'react'
import { upsertDecision } from '../services/gates/gateDecisionService'

/**
 * @typedef {{ content: string, appliedAt: string, issueId: string }} PendingPatch
 */

export function useIssueResolution() {
  // { [issueId]: 'pending_patch' | 'deferred' | 'suppressed' | 'stubbed' | 'external' | null }
  const [resolutions, setResolutions] = useState({})

  // { [issueId]: PendingPatch }
  const [pendingPatches, setPendingPatches] = useState({})

  /**
   * Hydrate the resolution map from decisions loaded from the API.
   * Call this once on mount after fetchDecisions() returns.
   *
   * @param {Array}  dbDecisions   - rows from GET /gate-decisions/{account}/{app}
   * @param {Array}  issues        - current normalized Issue[] (to match fingerprint → id)
   */
  const preloadDecisions = useCallback((dbDecisions, issues) => {
    if (!dbDecisions?.length || !issues?.length) return

    // Build fingerprint → issue map
    const fpToIssue = {}
    for (const issue of issues) {
      if (issue.fingerprint) fpToIssue[issue.fingerprint] = issue
    }

    let matched = 0
    setResolutions(prev => {
      const next = { ...prev }
      for (const d of dbDecisions) {
        const issue = fpToIssue[d.fingerprint]
        if (!issue) continue
        matched++
        if      (d.decision_type === 'SUPPRESS')              next[issue.id] = 'suppressed'
        else if (d.decision_type === 'MARK_TODO_STUB')        next[issue.id] = 'deferred'
        else if (d.decision_type === 'CUSTOM_EDIT')           next[issue.id] = 'pending_patch'
        else if (d.decision_type === 'GENERATE_COMPILE_STUB') next[issue.id] = 'stubbed'
        else if (d.decision_type === 'EXTERNAL_DEPENDENCY')   next[issue.id] = 'external'
      }
      return next
    })
    console.log(` [useIssueResolution] preloadDecisions: ${dbDecisions.length} from DB, ${matched} matched current issues`)
  }, [])

  /**
   * Record a fix for an issue (local + persist).
   *
   * @param {string} issueId
   * @param {object} patch         - { content, start_line, end_line }
   * @param {object} [apiContext]  - { account_id, application_name, gate_type, user_id, issue }
   */
  const applyFix = useCallback((issueId, patch = {}, apiContext = null) => {
    const record = {
      issueId,
      content: patch.content || '',
      appliedAt: new Date().toISOString(),
    }
    setPendingPatches(prev => ({ ...prev, [issueId]: record }))
    setResolutions(prev => ({ ...prev, [issueId]: 'pending_patch' }))

    // Persist to API (fire-and-forget)
    if (apiContext?.account_id && apiContext?.application_name && apiContext?.issue?.fingerprint) {
      const patchPayload = {}
      if (patch.start_line != null) patchPayload.start_line = patch.start_line
      if (patch.end_line != null)   patchPayload.end_line   = patch.end_line
      // Accept both field names: callers may pass `content` or `replacement_text`
      const replText = patch.replacement_text || patch.content || null
      if (replText)                 patchPayload.replacement_text = replText
      // Anchoring context for line-drift-safe patching (compile gate)
      if (patch.original_line)          patchPayload.original_line = patch.original_line
      if (patch.context_before?.length) patchPayload.context_before = patch.context_before
      if (patch.context_after?.length)  patchPayload.context_after = patch.context_after

      const decisionPayload = {
        account_id:       apiContext.account_id,
        application_name: apiContext.application_name,
        fingerprint:      apiContext.issue.fingerprint,
        gate_type:        apiContext.gate_type,
        decision_type:    'CUSTOM_EDIT',
        target_file:      apiContext.issue.javaLocation?.file || null,
        target_symbol:    null,
        patch_json:       JSON.stringify(patchPayload),
        user_id:          apiContext.user_id || null,
        gate_job_id:      apiContext.gate_job_id || null,
      }
      console.log(' [useIssueResolution] applyFix → upsertDecision', {
        fingerprint: decisionPayload.fingerprint,
        gate_type: decisionPayload.gate_type,
        gate_job_id: decisionPayload.gate_job_id,
        target_file: decisionPayload.target_file,
        patchPayload,
      })
      upsertDecision(decisionPayload)
        .then(r => console.log(' [useIssueResolution] applyFix persisted:', r))
        .catch(err => console.warn('[useIssueResolution] applyFix persist failed:', err))
    }
  }, [])

  /**
   * Mark an issue as deferred (TODO stub) — local + persist.
   *
   * @param {string} issueId
   * @param {object} [apiContext]  - { account_id, application_name, gate_type, user_id, issue }
   */
  const markDeferred = useCallback((issueId, apiContext = null) => {
    setResolutions(prev => ({ ...prev, [issueId]: 'deferred' }))

    // Persist to API (fire-and-forget)
    if (apiContext?.account_id && apiContext?.application_name && apiContext?.issue?.fingerprint) {
      const line = apiContext.issue.javaLocation?.line
      const decisionPayload = {
        account_id:       apiContext.account_id,
        application_name: apiContext.application_name,
        fingerprint:      apiContext.issue.fingerprint,
        gate_type:        apiContext.gate_type,
        decision_type:    'MARK_TODO_STUB',
        target_file:      apiContext.issue.javaLocation?.file || null,
        target_symbol:    null,
        patch_json:       JSON.stringify({
          insert_after_line: line ?? 0,
          stub_text: `    // TODO: ${apiContext.issue.summary || 'manual resolution required'}`,
        }),
        user_id:     apiContext.user_id || null,
        gate_job_id: apiContext.gate_job_id || null,
      }
      console.log(' [useIssueResolution] markDeferred → upsertDecision', {
        fingerprint: decisionPayload.fingerprint,
        gate_type: decisionPayload.gate_type,
        gate_job_id: decisionPayload.gate_job_id,
        target_file: decisionPayload.target_file,
        insert_after_line: line,
      })
      upsertDecision(decisionPayload)
        .then(r => console.log(' [useIssueResolution] markDeferred persisted:', r))
        .catch(err => console.warn('[useIssueResolution] markDeferred persist failed:', err))
    }
  }, [])

  /**
   * Save a GENERATE_COMPILE_STUB decision — tells the worker to create a compile-only
   * stub (class, method, field, or comment-import) on the next gate rerun.
   *
   * @param {string} issueId
   * @param {object} [apiContext]   - { account_id, application_name, gate_type, user_id, gate_job_id, issue }
   * @param {object} [stubDetails] - { category, symbol, file, line, message, strategy, notes }
   */
  const generateStub = useCallback((issueId, apiContext = null, stubDetails = {}) => {
    setResolutions(prev => ({ ...prev, [issueId]: 'stubbed' }))

    if (apiContext?.account_id && apiContext?.application_name && apiContext?.issue?.fingerprint) {
      const decisionPayload = {
        account_id:       apiContext.account_id,
        application_name: apiContext.application_name,
        fingerprint:      apiContext.issue.fingerprint,
        gate_type:        apiContext.gate_type,
        decision_type:    'GENERATE_COMPILE_STUB',
        target_file:      apiContext.issue.javaLocation?.file || null,
        target_symbol:    stubDetails.symbol || null,
        patch_json:       JSON.stringify({
          category: stubDetails.category || null,
          symbol:   stubDetails.symbol   || null,
          file:     stubDetails.file     || apiContext.issue.javaLocation?.file || null,
          line:     stubDetails.line     || apiContext.issue.javaLocation?.line || null,
          message:  stubDetails.message  || apiContext.issue.summary || null,
          strategy: stubDetails.strategy || 'create_stub_class',
          notes:    'compile-only',
        }),
        user_id:     apiContext.user_id     || null,
        gate_job_id: apiContext.gate_job_id || null,
      }
      console.log(' [useIssueResolution] generateStub → upsertDecision', {
        fingerprint: decisionPayload.fingerprint,
        gate_type:   decisionPayload.gate_type,
        gate_job_id: decisionPayload.gate_job_id,
        category:    stubDetails.category,
        symbol:      stubDetails.symbol,
        strategy:    stubDetails.strategy,
      })
      upsertDecision(decisionPayload)
        .then(r => console.log(' [useIssueResolution] generateStub persisted:', r))
        .catch(err => console.warn('[useIssueResolution] generateStub persist failed:', err))
    }
  }, [])

  /**
   * Record an EXTERNAL_DEPENDENCY decision — notes that a symbol belongs to an
   * external library.  No code is fabricated.  Worker records the requirement.
   *
   * @param {string} issueId
   * @param {object} [apiContext]       - { account_id, application_name, gate_type, user_id, gate_job_id, issue }
   * @param {object} [externalDetails] - { symbol, group, artifact, version, notes }
   */
  const markExternal = useCallback((issueId, apiContext = null, externalDetails = {}) => {
    setResolutions(prev => ({ ...prev, [issueId]: 'external' }))

    if (apiContext?.account_id && apiContext?.application_name && apiContext?.issue?.fingerprint) {
      const decisionPayload = {
        account_id:       apiContext.account_id,
        application_name: apiContext.application_name,
        fingerprint:      apiContext.issue.fingerprint,
        gate_type:        apiContext.gate_type,
        decision_type:    'EXTERNAL_DEPENDENCY',
        target_file:      apiContext.issue.javaLocation?.file || null,
        target_symbol:    externalDetails.symbol || apiContext.issue.symbolName || null,
        patch_json:       JSON.stringify({
          symbol:   externalDetails.symbol   || apiContext.issue.symbolName || null,
          group:    externalDetails.group    || null,
          artifact: externalDetails.artifact || null,
          version:  externalDetails.version  || null,
          notes:    externalDetails.notes    || 'requires external jar — not yet added to pom.xml',
        }),
        user_id:     apiContext.user_id     || null,
        gate_job_id: apiContext.gate_job_id || null,
      }
      console.log(' [useIssueResolution] markExternal → upsertDecision', {
        fingerprint: decisionPayload.fingerprint,
        gate_type:   decisionPayload.gate_type,
        gate_job_id: decisionPayload.gate_job_id,
        symbol:      externalDetails.symbol,
      })
      upsertDecision(decisionPayload)
        .then(r => console.log(' [useIssueResolution] markExternal persisted:', r))
        .catch(err => console.warn('[useIssueResolution] markExternal persist failed:', err))
    }
  }, [])

  /**
   * Clear a resolution (re-open an issue).
   */
  const clearResolution = useCallback((issueId) => {
    setResolutions(prev => {
      const next = { ...prev }
      delete next[issueId]
      return next
    })
    setPendingPatches(prev => {
      const next = { ...prev }
      delete next[issueId]
      return next
    })
  }, [])

  /**
   * Clear ALL resolutions and pending patches — used when switching gate jobs
   * or when Strict Check requests a raw (decision-free) view of compile state.
   */
  const clearAllResolutions = useCallback(() => {
    setResolutions({})
    setPendingPatches({})
  }, [])

  /**
   * Get resolution string for a single issue ('pending_patch', 'deferred', 'stubbed', etc. or null).
   */
  const getResolution = useCallback((issueId) => resolutions[issueId] || null, [resolutions])

  /**
   * Count of issues that have no resolution yet.
   * @param {Array<{id: string}>} issues
   */
  const unresolvedCount = useCallback(
    (issues) => issues.filter(i => !resolutions[i.id]).length,
    [resolutions]
  )

  return {
    resolutions,
    pendingPatches,
    applyFix,
    markDeferred,
    generateStub,
    markExternal,
    clearResolution,
    clearAllResolutions,
    getResolution,
    unresolvedCount,
    preloadDecisions,
  }
}
